import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import { Location, NgIf, NgFor } from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {BaseComponent} from '../../core/base.component';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import {AuthService} from '../../core/security/auth.service';
import {ProMessagingAutomationService} from '../pro-messaging-automation.service';
import {DateUtils} from '../../core/utilities/date-time-helpers';
import {RolesEnum} from '../../core/security/roles.enum';
import {AutomationModel, AutomationPhoneNumberModel, AutomationScheduleModel, LookupModel} from '../../api/models';
import moment from 'moment';
import {MatDialog} from '@angular/material/dialog';
import {MatConfirmDialogComponent} from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import {requiredBy} from '../../core/validators/required-by.validator';
import {PhoneNumberPipe} from '../../core/pipes/phone-number.pipe';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatMultiselectComponent } from '../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatTextareaInputComponent } from '../../editors/mat-textarea-input/mat-textarea-input.component';
import { MatTimeInputComponent } from '../../editors/mat-time-input/mat-time-input.component';
import { FlexModule } from '@angular/flex-layout';
import { MatButton } from '@angular/material/button';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { WeekDayPipe } from '../../core/pipes/week-day.pipe';

@Component({
    selector: 'app-pro-messaging-automation-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatCard,
        MatCardTitle,
        MatCardContent,
        NgIf,
        MatTextInputComponent,
        MatCheckboxInputComponent,
        MatMultiselectComponent,
        MatTextareaInputComponent,
        NgFor,
        MatTimeInputComponent,
        MatCardActions,
        FlexModule,
        MatButton,
        Angular2PromiseButtonModule,
        MatTooltip,
        MatIcon,
        WeekDayPipe,
    ],
})
export class ProMessagingAutomationEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  model: AutomationModel = {
    name: '',
    id: 'new',
    isEnabled: true,
    keyword: [],
    replyMessage: '',
    schedule: [],
    recipients: [],
    phoneNumbers: []
  };
  buttonSubmitPromise: Promise<void>;
  availablePhoneNumbers: LookupModel[];
  phoneNumberDisplayPipe = new PhoneNumberPipe();
  private paramsSubscription;


  constructor(route: ActivatedRoute, titleService: Title, headerService: HeaderService, authService: AuthService,
              public dialog: MatDialog,
              public automationService: ProMessagingAutomationService,
              private fb: UntypedFormBuilder,
              private location: Location, private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.editRoles = [RolesEnum.MessagingAutomation_Edit, RolesEnum.MessagingAutomation_Delete];
    this.deleteRoles = [RolesEnum.MessagingAutomation_Delete];

    this.paramsSubscription = this.route.params.subscribe(async (params) => {
      this.model.id = params['id'];
      console.log(`model.id - ${this.model.id}`);

      this.availablePhoneNumbers = await this.automationService.lookupPhoneNumbers();

      if (this.model.id !== 'new') {
        this.model = await this.automationService.getAutomationItem(this.model.id);
        this.ngOnChanges();
      }
    });

    this.headerService.showBackButton(true);

    this.createForm();
  }

  get scheduleData(): UntypedFormArray {
    return this.form.get('schedule') as UntypedFormArray;
  }

  async ngOnInit() {
    super.ngOnInit();

    this.headerService.showBackButton(true);

    this.ngOnChanges();
  }

  ngOnChanges() {
    const phoneNumbersIds: string[] = (this.model.phoneNumbers || []).map(x => x.id);
    const phoneNumbers: string[] = (this.availablePhoneNumbers || [])
      .filter(x => phoneNumbersIds.indexOf(`${x.id}`) > -1)
      .map(x => `${x.id}`);

    this.form.reset({
      name: this.model.name,
      keyword: this.model.keyword,
      isEnabled: this.model.isEnabled,
      replyMessage: this.model.replyMessage,
      phoneNumbers: phoneNumbers,
    });

    this.setSchedule(this.model.schedule || []);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  async onSubmit() {
    this.submitted = false;
    this.serverError = false;

    if (this.form.valid) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {

    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.automationService.saveAutomation(reqData);
      this.notificationService.notifySuccess('Record updated successfully');
      this.goBack();
    } catch (e) {
      console.error(e);
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): AutomationModel {
    const formModel = this.form.value;

    const scheduleCopy: AutomationScheduleModel[] = formModel.schedule.map(
      (item: any) => {
        const startTime: Date = item.startDateTime?.toDate();
        const endTime: Date = item.endDateTime?.toDate();
        console.log(item.startTime, item.endTime);
        return {
          id: item.id,
          weekDay: item.weekDay,
          startTime: startTime ? DateUtils.dateTimeToMinutes(startTime) : undefined,
          endTime: endTime ? DateUtils.dateTimeToMinutes(endTime) : undefined,
          isEnabled: item.isEnabled,
        };
      }
    );

    const phoneNumbersIds: string[] = formModel.phoneNumbers || [];
    const phoneNumbers: AutomationPhoneNumberModel[] = (this.availablePhoneNumbers || [])
      .filter(x => phoneNumbersIds.indexOf(`${x.id}`) > -1)
      .map(x => {
        return {
          id: `${x.id}`,
          phoneNumber: x.text
        };
      });

    return {
      id: this.model.id,
      name: formModel.name,
      keyword: formModel.keyword,
      isEnabled: formModel.isEnabled,
      replyMessage: formModel.replyMessage,
      schedule: scheduleCopy,
      recipients: [],
      phoneNumbers: phoneNumbers
    };
  }

  setSchedule(items: AutomationScheduleModel[]) {

    items.sort((a, b) => {
      return a.weekDay - b.weekDay;
    });


    if (items.length === 0) {
      for (let i = 0; i < 7; i++) {
        items.push({
          id: '',
          isEnabled: false,
          weekDay: i,
        });
      }
    }

    const scheduleFormGroups = items.map(item => this.fb.group({
        'id': [item.id],
        'weekDay': [item.weekDay],
        'startDateTime': [item.startTime >= 0 ? moment(DateUtils.minutesToDateTime(item.startTime)) : undefined, [requiredBy('isEnabled')]],
        'endDateTime': [item.endTime >= 0 ? moment(DateUtils.minutesToDateTime(item.endTime)) : undefined, [requiredBy('isEnabled')]],
        'isEnabled': [item.isEnabled],
      },
      {validators: this.specificValueInsideRange.bind(this)}
    ));

    const formArray = this.fb.array(scheduleFormGroups);
    this.form.setControl('schedule', formArray);
  }

  specificValueInsideRange(group: UntypedFormGroup) {
    console.log(group);
    const startDateTime = group.value.startDateTime?.toDate();
    const endDateTime = group.value.endDateTime?.toDate();
    const endDateControl = group.controls['endDateTime'];
    const isEnabled = group.value.isEnabled;

    if (isEnabled) {
      const startTime = startDateTime
        ? startDateTime.getHours() * 60 + startDateTime.getMinutes()
        : undefined;
      const endTime = endDateTime
        ? endDateTime.getHours() * 60 + endDateTime.getMinutes()
        : undefined;

      console.log('time range', startTime, endTime);

      if (startTime >= endTime) {
        const errorObj = {
          endTimeMustBeGreaterThanStartTime: true
        };
        endDateControl.setErrors(Object.assign((endDateControl.errors || {}), errorObj));

        return errorObj;
      }
    }

    return null;
  }

  goBack(): void {
    this.location.back();
  }

  createForm(): void {

    this.form = this.fb.group({
        'name': ['', Validators.required],
        'isEnabled': [true],
        'keyword': [[]],
        'replyMessage': ['', Validators.required],
        'schedule': this.fb.array([]),
        'phoneNumbers': [[]],
      }
    );

    this.form.statusChanges.subscribe(value => {
      console.log(value, Date.now().toString());
    });
  }

  async onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Record?',
        text: 'Are you sure you want to delete this record?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.automationService.deleteAutomationItem(this.model.id);
        this.notificationService.notifySuccess('Record has been deleted successfully');
        this.headerService.goBack();
      }
    });
  }

  scheduleItemChanged($event: boolean, formItem: AbstractControl) {
    console.log($event);
    if (formItem instanceof UntypedFormGroup) {
      for (let i in formItem.controls) {
        formItem.controls[i].markAsTouched({onlySelf: true});
        formItem.controls[i].markAsDirty({onlySelf: true});
        formItem.controls[i].updateValueAndValidity();
      }
    }
  }
}
